import { Component, Inject, inject, signal, Signal, input, WritableSignal, effect } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, Subject, switchMap, take, tap, delay } from 'rxjs';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../tokens-and-interfaces';
import { CRMTrackingService } from '../../shared-services/page-analytics.service';
import { TranslationModule } from '../../i18n/translation-module';
import { CrmAIApiService } from '@vendasta/crm-integrations';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { startWith, filter } from 'rxjs/operators';
import { MatExpansionPanel, MatExpansionModule } from '@angular/material/expansion';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'crm-ai-summary',
  templateUrl: './ai-summary.component.html',
  styleUrls: ['./ai-summary.component.scss'],
  standalone: true,
  imports: [
    MatExpansionPanel,
    MatExpansionModule,
    AsyncPipe,
    TranslateModule,
    TranslationModule,
    GalaxyAlertModule,
    GalaxyLoadingSpinnerModule,
    MatIconModule,
  ],
})
export class CrmAISummaryComponent {
  crmObjectId = input<string>('');
  private readonly crmObjectId$ = toObservable(this.crmObjectId);
  crmObjectType = input<ObjectType | null>(null);
  private readonly crmObjectType$ = toObservable(this.crmObjectType);

  private aiSummaryTrigger$$ = new Subject<void>();
  private readonly regenerateAISummary = signal<boolean>(false);
  private changedIdSinceGeneration = false;

  protected readonly aiSummaryIsLoading = signal<boolean>(true);
  protected readonly noAISummary = signal(false);
  protected readonly showAISummaryRestrictedDialog = signal<boolean>(false);

  protected readonly openAISummaryExpansionPanel: WritableSignal<boolean> = signal(false);
  protected hasAISummaryParam: Signal<boolean>;
  protected readonly hasAccessToAISummary$ = this.config.hasAccessToFeature$?.('crm-ai-summary') ?? of(false);
  protected readonly aiSummaryResponse$: Observable<string>;

  private readonly translateService = inject(TranslateService);
  private readonly aiIconService = inject(GalaxyAiIconService);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly activatedRoute: ActivatedRoute,
    private readonly crmAIService: CrmAIApiService,
    private readonly snackbarService: SnackbarService,
    private readonly trackingService: CRMTrackingService,
  ) {
    this.aiIconService.dummyInit();

    this.showAISummaryRestrictedDialog.set(!!this.config.openRestrictedDialog);
    this.aiSummaryResponse$ = this.getAISummaryResponse();

    // expand AI summary panel at the start if aiSummary query param is true
    const hasAISummaryParam$ = this.activatedRoute.queryParamMap.pipe(
      take(1),
      map((params) => params.get('aiSummary') || ''),
      map((aiSummary) => aiSummary === 'true'),
      tap((hasAISummaryParam) => {
        if (hasAISummaryParam) {
          this.trackingService.trackEvent(this.crmObjectType() as ObjectType, 'used-ai-quick-summary-url-parameter');
        }
      }),
    );
    this.hasAISummaryParam = toSignal(hasAISummaryParam$, { initialValue: false });

    effect(() => {
      this.crmObjectId();
      if (this.hasAISummaryParam()) {
        this.hasAISummaryParam = signal(false);
        this.openAISummaryExpansionPanel.set(true);
      } else {
        this.openAISummaryExpansionPanel.set(false);
      }
    });

    effect(() => {
      this.crmObjectId();
      this.changedIdSinceGeneration = true;
    });
  }

  protected onOpenNoFeatureAISummary() {
    this.config.openRestrictedDialog?.('crm-ai-summary');
  }

  protected regenerateSummary(regenerateSummary: boolean) {
    this.regenerateAISummary.set(regenerateSummary);
    this.aiSummaryTrigger$$.next();
  }

  protected onExpandedChange(expanded: boolean): void {
    this.openAISummaryExpansionPanel.set(expanded);
  }

  private getAISummaryResponse(): Observable<string> {
    return combineLatest([
      this.crmObjectId$,
      this.crmObjectType$,
      this.config.namespace$,
      this.config.locale$ ?? of(''),
      toObservable(this.openAISummaryExpansionPanel),
      this.aiSummaryTrigger$$.pipe(startWith(undefined)),
    ]).pipe(
      tap(() => {
        if (this.shouldGenerateAISummary()) {
          this.aiSummaryIsLoading.set(true);
        }
      }),
      delay(100), // delay so that openAISummaryExpansionPanel is properly calculated before checking it
      filter(() => this.shouldGenerateAISummary()),
      switchMap(([crmObjectId, crmObjectType, namespace, locale]) => {
        const regenerateAISummary = this.regenerateAISummary();
        this.regenerateAISummary.set(false);
        if (!namespace || !crmObjectType || !crmObjectId || !this.openAISummaryExpansionPanel()) {
          return of('');
        }
        return this.crmAIService
          .getAiSummaryForCrmObject({
            crmObjectNamespace: namespace,
            crmObjectType: crmObjectType || '',
            crmObjectId: crmObjectId,
            language: locale,
            regenerateSummary: regenerateAISummary,
          })
          .pipe(
            map((response) => response?.response),
            tap((response) => {
              if (!response) {
                this.noAISummary.set(true);
              } else {
                this.noAISummary.set(false);
              }
              this.changedIdSinceGeneration = false;
              this.trackingService.trackEvent(crmObjectType as ObjectType, 'loaded-crm-object-ai-quick-summary');
            }),
            map((response) => {
              if (this.noAISummary()) {
                return this.translateService.instant('AI_SUMMARY.NO_SUMMARY');
              }
              const parser = new DOMParser();
              return parser.parseFromString(response, 'text/html').body.innerHTML;
            }),
            catchError(() => {
              this.snackbarService.openErrorSnack('AI_SUMMARY.ERROR');
              this.trackingService.trackEvent(crmObjectType as ObjectType, 'failed-load-crm-object-ai-quick-summary');
              return of('');
            }),
          );
      }),
      tap(() => this.aiSummaryIsLoading.set(false)),
    );
  }

  private shouldGenerateAISummary(): boolean {
    return this.regenerateAISummary() || (this.openAISummaryExpansionPanel() && this.changedIdSinceGeneration);
  }
}
